import './ManageAddressesTab.css';
import BlockUi from '@availity/block-ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Divider, Form, Input } from 'semantic-ui-react';
import { addToBlackList, addToWhiteList, deleteFromBlackList, findAddress, getBlackWhiteList } from '../../actions/blackWhiteList';
import { AddressItem, BlackWhiteListItem } from '../../types/blackWhiteListItemType';
import SemanticDataTable, { SemanticDataTableColumn } from '../shared/semanticTable/SemanticDataTable';
import MobileSecuritySettingsSection from './MobileSecuritySettingsSection';

const tableSectionStyle: React.CSSProperties = {
    width: '100%',
};

const wideFormStyle: React.CSSProperties = {
    width: '100%',
};

const tableMinHeight = '10rem';

const columnStyles: Record<string, React.CSSProperties> = {
    address: { width: '45%', minWidth: '8rem' },
    coordinates: { width: '15%', minWidth: '5rem' },
    alias: { width: '18%', minWidth: '6rem' },
    actionWide: { width: '18%', minWidth: '6.5rem' },
    actionNarrow: { width: '10%', minWidth: '3rem' },
};

const renderDeleteButton = (onDelete: () => Promise<void>) => (
    <Button
        type="button"
        basic
        compact
        icon="trash"
        size="mini"
        aria-label="Delete row"
        onClick={(event) => {
            event.stopPropagation();
            void onDelete();
        }}
    />
);

const ManageAddressesTab: React.FC = () => {
    const [formBlockingState, setFormBlockingState] = useState(true);
    const [addressState, setAddressState] = useState('');
    const [searchAddressListState, setSearchAddressListState] = useState<AddressItem[]>([]);
    const [blackWhiteListState, setBlackWhiteListState] = useState<BlackWhiteListItem[]>([]);
    const [whiteListState, setWhiteListState] = useState<BlackWhiteListItem[]>([]);

    const onSearchAddressHandler = useCallback(async (data: string) => await findAddress(data), []);
    const onAddToBlackListHandler = useCallback(async (data: BlackWhiteListItem) => await addToBlackList(data), []);
    const onAddToWhiteListHandler = useCallback(async (data: BlackWhiteListItem) => await addToWhiteList(data), []);
    const onDeleteFromBlackListHandler = useCallback(async (id: string) => await deleteFromBlackList(id), []);

    const updateTables = useCallback(async () => {
        setFormBlockingState(true);

        try {
            const result = await getBlackWhiteList();
            setBlackWhiteListState(result.filter((item) => item.isBlack));
            setWhiteListState(result.filter((item) => !item.isBlack));
        } finally {
            setFormBlockingState(false);
        }
    }, []);

    const onSearchHandler = useCallback(async () => {
        if (!addressState.trim()) {
            setSearchAddressListState([]);
            return;
        }

        setFormBlockingState(true);

        try {
            const value = await onSearchAddressHandler(addressState);
            setSearchAddressListState(value);
        } finally {
            setFormBlockingState(false);
        }
    }, [addressState, onSearchAddressHandler]);

    const onAddressChangeHandle = useCallback((value: string) => {
        setAddressState(value);
    }, []);

    const onAddWhiteListHandleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;
        const newEntry: BlackWhiteListItem = {
            id: '',
            rawId: '',
            address: (form.elements.namedItem('address') as HTMLInputElement).value,
            keywords: (form.elements.namedItem('keywords') as HTMLInputElement).value,
            lat: parseFloat((form.elements.namedItem('lat') as HTMLInputElement).value),
            lon: parseFloat((form.elements.namedItem('lon') as HTMLInputElement).value),
            isBlack: false,
        };

        setFormBlockingState(true);

        try {
            await onAddToWhiteListHandler(newEntry);
            await updateTables();
            form.reset();
        } finally {
            setFormBlockingState(false);
        }
    }, [onAddToWhiteListHandler, updateTables]);

    useEffect(() => {
        void updateTables();
    }, [updateTables]);

    const searchColumns = useMemo<SemanticDataTableColumn<AddressItem>[]>(() => ([
        {
            id: 'address',
            title: 'Address',
            render: (row) => row.address,
            getSortValue: (row) => row.address,
            getFilterValue: (row) => row.address,
            headerStyle: columnStyles.address,
            cellStyle: columnStyles.address,
            filter: {
                type: 'text',
                placeholder: 'Address...',
                debounceMs: 250,
            },
        },
        {
            id: 'lat',
            title: 'Lat',
            render: (row) => row.lat,
            getSortValue: (row) => row.lat,
            getFilterValue: (row) => row.lat,
            headerStyle: columnStyles.coordinates,
            cellStyle: columnStyles.coordinates,
            filter: {
                type: 'text',
                placeholder: 'Lat...',
                debounceMs: 250,
            },
        },
        {
            id: 'lon',
            title: 'Lon',
            render: (row) => row.lon,
            getSortValue: (row) => row.lon,
            getFilterValue: (row) => row.lon,
            headerStyle: columnStyles.coordinates,
            cellStyle: columnStyles.coordinates,
            filter: {
                type: 'text',
                placeholder: 'Lon...',
                debounceMs: 250,
            },
        },
        {
            id: 'actions',
            title: '',
            render: (row) => (
                <Button
                    type="button"
                    primary
                    compact
                    size="mini"
                    content="Add to black"
                    onClick={async (event) => {
                        event.stopPropagation();
                        const newEntity: BlackWhiteListItem = {
                            id: row.id,
                            rawId: row.rawId,
                            address: row.address,
                            keywords: '',
                            lat: row.lat,
                            lon: row.lon,
                            isBlack: true,
                        };

                        setFormBlockingState(true);

                        try {
                            await onAddToBlackListHandler(newEntity);
                            await updateTables();
                        } finally {
                            setFormBlockingState(false);
                        }
                    }}
                />
            ),
            sortable: false,
            headerStyle: columnStyles.actionWide,
            cellStyle: columnStyles.actionWide,
            textAlign: 'center',
        },
    ]), [onAddToBlackListHandler, updateTables]);

    const blackListColumns = useMemo<SemanticDataTableColumn<BlackWhiteListItem>[]>(() => ([
        {
            id: 'address',
            title: 'Address',
            render: (row) => row.address,
            getSortValue: (row) => row.address,
            getFilterValue: (row) => row.address,
            headerStyle: columnStyles.address,
            cellStyle: columnStyles.address,
            filter: {
                type: 'text',
                placeholder: 'Address...',
                debounceMs: 250,
            },
        },
        {
            id: 'lat',
            title: 'Lat',
            render: (row) => row.lat,
            getSortValue: (row) => row.lat,
            getFilterValue: (row) => row.lat,
            headerStyle: columnStyles.coordinates,
            cellStyle: columnStyles.coordinates,
            filter: {
                type: 'text',
                placeholder: 'Lat...',
                debounceMs: 250,
            },
        },
        {
            id: 'lon',
            title: 'Lon',
            render: (row) => row.lon,
            getSortValue: (row) => row.lon,
            getFilterValue: (row) => row.lon,
            headerStyle: columnStyles.coordinates,
            cellStyle: columnStyles.coordinates,
            filter: {
                type: 'text',
                placeholder: 'Lon...',
                debounceMs: 250,
            },
        },
        {
            id: 'actions',
            title: '',
            render: (row) => renderDeleteButton(async () => {
                await onDeleteFromBlackListHandler(row.id);
                await updateTables();
            }),
            sortable: false,
            headerStyle: columnStyles.actionNarrow,
            cellStyle: columnStyles.actionNarrow,
            textAlign: 'center',
        },
    ]), [onDeleteFromBlackListHandler, updateTables]);

    const whiteListColumns = useMemo<SemanticDataTableColumn<BlackWhiteListItem>[]>(() => ([
        {
            id: 'address',
            title: 'Address',
            render: (row) => row.address,
            getSortValue: (row) => row.address,
            getFilterValue: (row) => row.address,
            headerStyle: columnStyles.address,
            cellStyle: columnStyles.address,
            filter: {
                type: 'text',
                placeholder: 'Address...',
                debounceMs: 250,
            },
        },
        {
            id: 'lat',
            title: 'Lat',
            render: (row) => row.lat,
            getSortValue: (row) => row.lat,
            getFilterValue: (row) => row.lat,
            headerStyle: columnStyles.coordinates,
            cellStyle: columnStyles.coordinates,
            filter: {
                type: 'text',
                placeholder: 'Lat...',
                debounceMs: 250,
            },
        },
        {
            id: 'lon',
            title: 'Lon',
            render: (row) => row.lon,
            getSortValue: (row) => row.lon,
            getFilterValue: (row) => row.lon,
            headerStyle: columnStyles.coordinates,
            cellStyle: columnStyles.coordinates,
            filter: {
                type: 'text',
                placeholder: 'Lon...',
                debounceMs: 250,
            },
        },
        {
            id: 'keywords',
            title: 'Alias',
            render: (row) => row.keywords,
            getSortValue: (row) => row.keywords,
            getFilterValue: (row) => row.keywords,
            headerStyle: columnStyles.alias,
            cellStyle: columnStyles.alias,
            filter: {
                type: 'text',
                placeholder: 'Alias...',
                debounceMs: 250,
            },
        },
        {
            id: 'actions',
            title: '',
            render: (row) => renderDeleteButton(async () => {
                await onDeleteFromBlackListHandler(row.id);
                await updateTables();
            }),
            sortable: false,
            headerStyle: columnStyles.actionNarrow,
            cellStyle: columnStyles.actionNarrow,
            textAlign: 'center',
        },
    ]), [onDeleteFromBlackListHandler, updateTables]);

    return (
        <div className="manageAddressesPage">
            <BlockUi tag="div" message="preparing..." blocking={formBlockingState}>
                <Form className="manageAddressesTab" style={wideFormStyle} onSubmit={onAddWhiteListHandleSubmit}>
                <h3 style={tableSectionStyle}>Black list</h3>
                <Form.Group>
                    <Form.Field style={{ width: '100%' }}>
                        <SemanticDataTable<BlackWhiteListItem>
                            data={blackWhiteListState}
                            columns={blackListColumns}
                            rowKey={(row) => row.id}
                            width="100%"
                            minHeight={tableMinHeight}
                            maxHeight="calc((100vh - 24rem) / 3)"
                            pageSize={10}
                            emptyMessage="No black list entries found."
                        />
                    </Form.Field>
                </Form.Group>
                <Form.Group>
                    <Form.Field>
                        <Input
                            labelPosition="left"
                            placeholder="Address"
                            value={addressState}
                            onChange={(event) => onAddressChangeHandle(event.target.value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Button
                            type="button"
                            key="searchButton"
                            primary
                            content="Search"
                            onClick={() => {
                                void onSearchHandler();
                            }}
                            className="primaryButton"
                        />
                    </Form.Field>
                    <Form.Field width={10} />
                </Form.Group>
                <Form.Group>
                    <Form.Field style={{ width: '100%' }}>
                        <SemanticDataTable<AddressItem>
                            data={searchAddressListState}
                            columns={searchColumns}
                            rowKey={(row) => `${row.id}-${row.rawId}`}
                            width="100%"
                            minHeight={tableMinHeight}
                            maxHeight="calc((100vh - 24rem) / 3)"
                            pageSize={10}
                            emptyMessage="No search results found."
                        />
                    </Form.Field>
                </Form.Group>

                <h3 style={tableSectionStyle}>White list</h3>
                <Form.Group grouped widths="equal">
                    <Form.Field>
                        <label>Address</label>
                        <Input
                            placeholder="Insert address"
                            name="address"
                            required
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Alias</label>
                        <Input
                            placeholder="Insert Alias"
                            name="keywords"
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Latitude </label>
                        <Input
                            placeholder="Insert latitude"
                            name="lat"
                            type="number"
                            step="any"
                            required
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Longitude</label>
                        <Input
                            placeholder="Insert longitude"
                            name="lon"
                            type="number"
                            step="any"
                            required
                        />
                    </Form.Field>

                    <Button type="submit" style={{
                        margin: '0',
                        marginTop: '14px',
                        marginLeft: '0',
                    }} primary>
                        Add to white list
                    </Button>
                </Form.Group>
                <Form.Group>
                    <Form.Field className="whiteListTableField" style={{ width: '100%' }}>
                        <SemanticDataTable<BlackWhiteListItem>
                            data={whiteListState}
                            columns={whiteListColumns}
                            rowKey={(row) => row.id}
                            width="100%"
                            minHeight={tableMinHeight}
                            maxHeight="calc((100vh - 24rem) / 3)"
                            pageSize={10}
                            emptyMessage="No white list entries found."
                        />
                    </Form.Field>
                </Form.Group>
            </Form>
                <Divider horizontal />
                <MobileSecuritySettingsSection />
            </BlockUi>
        </div>
    );
};

export default ManageAddressesTab;
